import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";

import { useGetRunsHistory, useCancelRun } from "../api";
import { DATASET_DESTINATION, MLExportRun } from "../types/run";
import { Link } from "react-router-dom";
import { Button } from "@/ui/components/ui/button";
import { Skeleton } from "@/ui/components/ui/skeleton";
import {
  XCircle,
  ChevronRight,
  Clock,
  Loader2,
  CheckCircle2,
  AlertTriangle,
  Ban,
} from "lucide-react";
import { cn } from "@/ui/lib/utils";
import { useOrganizationStore } from "@/shared/stores/useOrganizationStore";
import { useGetOrganizationById } from "@/auth/api/queries/user.queries";
import { useSetBreadcrumbs } from "@/shared/contexts/breadcrumb-context";
import { ML_EXPORTS_URL } from "../config/ml-exports-urls";
import {
  formatDateTime,
  formatNumber,
} from "@/shared/lib/i18n/formatters.ts";
import toast from "react-hot-toast";

/** Run status codes are wire values; only the badge text is translated. */
type RunStatusLabelKey =
  | "runStatus.pending"
  | "runStatus.running"
  | "runStatus.success"
  | "runStatus.partial"
  | "runStatus.failed"
  | "runStatus.cancelled";

const STATUS_CONFIG: Record<string, {
  icon: React.ComponentType<{ className?: string }>;
  labelKey: RunStatusLabelKey;
  badge: string;
  stripe: string;
  animate?: boolean;
}> = {
  PENDING: {
    icon: Clock,
    labelKey: "runStatus.pending",
    badge: "bg-slate-500/10 text-slate-600 dark:text-slate-300 border-slate-500/20",
    stripe: "bg-slate-400",
  },
  RUNNING: {
    icon: Loader2,
    labelKey: "runStatus.running",
    badge: "bg-blue-500/10 text-blue-500 border-blue-500/20",
    stripe: "bg-blue-500",
    animate: true,
  },
  SUCCESS: {
    icon: CheckCircle2,
    labelKey: "runStatus.success",
    badge: "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/20",
    stripe: "bg-emerald-500",
  },
  PARTIAL: {
    icon: AlertTriangle,
    labelKey: "runStatus.partial",
    badge: "bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/20",
    stripe: "bg-amber-500",
  },
  FAILED: {
    icon: AlertTriangle,
    labelKey: "runStatus.failed",
    badge: "bg-red-500/10 text-red-500 border-red-500/20",
    stripe: "bg-red-500",
  },
  CANCELLED: {
    icon: Ban,
    labelKey: "runStatus.cancelled",
    badge: "bg-zinc-500/10 text-zinc-500 dark:text-zinc-400 border-zinc-500/20",
    stripe: "bg-zinc-400",
  },
};

const RunStatusBadge = ({
  status,
  t,
}: {
  status: string;
  t: TFunction<"mlExports">;
}) => {
  const cfg = STATUS_CONFIG[status];
  if (!cfg) return <span className="text-xs text-muted-foreground">{status}</span>;
  const Icon = cfg.icon;
  return (
    <span className={cn("inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border", cfg.badge)}>
      <Icon className={cn("h-3 w-3", cfg.animate && "animate-spin")} />
      {t(cfg.labelKey)}
    </span>
  );
};

const ExportRunsPage = () => {
  const { t } = useTranslation("mlExports");
  const { selectedOrganizationId } = useOrganizationStore();
  const { data: orgData, isLoading: isOrgLoading } = useGetOrganizationById(selectedOrganizationId!);

  useSetBreadcrumbs([
    {
      path: ML_EXPORTS_URL.ROOT,
      breadcrumb: orgData?.data?.name || t("breadcrumbs.organization"),
      type: "organizations",
      currentId: selectedOrganizationId!,
      isLoading: isOrgLoading,
    },
    {
      path: ML_EXPORTS_URL.RUNS,
      breadcrumb: t("breadcrumbs.runs"),
    },
  ]);

  const { data: runs, isLoading } = useGetRunsHistory(50);
  const cancelMutation = useCancelRun();

  const handleCancel = async (runId: string) => {
    try {
      await cancelMutation.mutateAsync(runId);
      toast.success(t("runs.cancelRequested"));
    } catch {
      toast.error(t("runs.cancelFailed"));
    }
  };

  if (isLoading) {
    return (
      <div className="p-6 space-y-4">
        <Skeleton className="h-10 w-64" />
        {Array.from({ length: 5 }).map((_, i) => (
          <Skeleton key={i} className="h-16 w-full" />
        ))}
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <div>
        <h1 className="text-2xl font-bold">{t("runs.title")}</h1>
        <p className="text-sm text-muted-foreground">
          {t("runs.description")}
        </p>
      </div>

      {(!runs || runs.length === 0) && (
        <p className="text-muted-foreground">{t("runs.empty")}</p>
      )}

      <div className="space-y-2">
        {runs?.map((run: MLExportRun) => {
          const isActive = !run.status.is_terminal;
          const locationName = run.summary.selection?.location?.name;
          const machineCount = run.summary.selection?.machines?.length ?? 0;

          return (
            <Link
              key={run.run_id}
              to={`/ml-exports/runs/${run.run_id}`}
              className="group flex items-center border rounded-lg bg-card hover:bg-accent/50 transition-colors cursor-pointer overflow-hidden"
            >
              {/* Color stripe */}
              <div className={cn(
                "w-1 self-stretch shrink-0",
                STATUS_CONFIG[run.status.code]?.stripe ?? "bg-muted",
              )} />

              <div className="flex items-center justify-between flex-1 p-4 min-w-0">
                <div className="flex items-center gap-4 min-w-0">
                  <RunStatusBadge status={run.status.code} t={t} />
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-sm truncate">
                        {run.destination.label}
                        {run.destination.project_id && (
                          <span className="text-muted-foreground font-normal"> / {run.destination.project_id}</span>
                        )}
                      </span>
                      {/* Manifest runs copy no images, so their uploaded/duration
                          figures read as a suspiciously small provider export.
                          The tag makes them scannable for what they are. */}
                      {run.destination.code === DATASET_DESTINATION && (
                        <span className="shrink-0 px-1.5 py-0.5 rounded text-[9px] font-semibold uppercase tracking-wider bg-muted text-muted-foreground border">
                          {t("runs.datasetTag")}
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-muted-foreground truncate">
                      {run.timing.created_at &&
                        formatDateTime(new Date(run.timing.created_at))}
                      {locationName && ` \u00b7 ${locationName}`}
                      {machineCount > 0 &&
                        ` \u00b7 ${t("runs.machineCount", { count: machineCount, value: formatNumber(machineCount) })}`}
                      {run.summary.uploaded != null && run.summary.uploaded > 0 &&
                        ` \u00b7 ${t("runs.uploadedCount", { value: formatNumber(run.summary.uploaded) })}`}
                      {run.summary.failed != null && run.summary.failed > 0 &&
                        ` \u00b7 ${t("runs.failedCount", { value: formatNumber(run.summary.failed) })}`}
                      {run.timing.duration_seconds != null &&
                        ` \u00b7 ${t("runs.durationSeconds", { value: formatNumber(run.timing.duration_seconds, { minimumFractionDigits: 1, maximumFractionDigits: 1 }) })}`}
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-2 shrink-0">
                  {isActive && run.progress.percent != null && run.progress.percent > 0 && (
                    <span className="text-xs font-medium text-blue-500 tabular-nums">
                      {t("runs.percent", {
                        value: formatNumber(Math.round(run.progress.percent)),
                      })}
                    </span>
                  )}
                  {isActive && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleCancel(run.run_id);
                      }}
                      disabled={cancelMutation.isPending}
                    >
                      <XCircle className="h-4 w-4" />
                    </Button>
                  )}
                  <ChevronRight className="h-4 w-4 text-muted-foreground group-hover:text-foreground transition-colors" />
                </div>
              </div>
            </Link>
          );
        })}
      </div>
    </div>
  );
};

export default ExportRunsPage;
